import React, {useState} from 'react';
import LobbyPlayerName from '../LobbyPlayerName';
import LobbyDesktopPlayerBadges from './LobbyDesktopPlayerBadges';
import LobbyFactionPicker, {LOBBY_FACTION_PICKER_VIEWS} from './LobbyFactionPicker';
import LobbyFactionTiles from './LobbyFactionTiles';
import {getLobbyFactionDisplay} from './lobbyFactionDisplay';
import PageButton from '../../elements/PageButton/PageButton';
import {RANDOM_FACTION_ID} from '../../../configs/factions';
import {MENU_SOUND_MANAGER} from '../../../scenes/MenuSoundManager';

// Выбранный вид встроенного выбора фракции — удобство одного зрителя, не
// состояние лобби: живет в браузере и может не сохраниться (приватный режим)
const PICKER_VIEW_STORAGE_KEY = 'lobbyFactionPickerView';

const readStoredPickerView = () => {
  try {
    const storedView = window.localStorage.getItem(PICKER_VIEW_STORAGE_KEY);
    return Object.values(LOBBY_FACTION_PICKER_VIEWS).includes(storedView)
      ? storedView
      : LOBBY_FACTION_PICKER_VIEWS.CAROUSEL;
  } catch (e) {
    return LOBBY_FACTION_PICKER_VIEWS.CAROUSEL;
  }
};

const storePickerView = (view) => {
  try {
    window.localStorage.setItem(PICKER_VIEW_STORAGE_KEY, view);
  } catch (e) {
    // Хранилище недоступно — вид просто не запомнится
  }
};

// Переключатель — значками, а не словами: в шапке рядом с ником и бейджами
// для подписей нет места, и длинный ник обрезался бы. Подпись остается в
// подсказке и для экранных чтецов
const PICKER_VIEW_OPTIONS = [
  {
    view: LOBBY_FACTION_PICKER_VIEWS.CAROUSEL,
    label: 'Карусель',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect x="1" y="7" width="4" height="10" rx="0.5"/>
        <rect x="7" y="3" width="10" height="18" rx="0.5"/>
        <rect x="19" y="7" width="4" height="10" rx="0.5"/>
      </svg>
    ),
  },
  {
    view: LOBBY_FACTION_PICKER_VIEWS.GRID,
    label: 'Все фракции',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        {[2, 9.5, 17].flatMap((y) => [2, 9.5, 17].map((x) => (
          <rect key={`${x}_${y}`} x={x} y={y} width="5" height="5" rx="0.5"/>
        )))}
      </svg>
    ),
  },
];

/**
 * Подсказка под своими плитками рейтингового лобби: что сейчас делает нажатие
 * и как выбор становится решением (см. utils/lobby/rankFactionConfirm).
 */
const getOwnRankedFactionsHint = ({actingPlayer, banView, selectView}) => {
  if (banView.isPhase) {
    return 'Идут баны — голоса соперников видны на ваших фракциях';
  }
  if (selectView.isOwnConfirmed) {
    return 'Выбор подтвержден. Сменить фракцию — «Изменить выбор»';
  }
  if (selectView.isConfirmer) {
    return actingPlayer.factionId
      ? 'До подтверждения выбор можно менять. Не подтвердите — сыграете выбранной'
      : 'Нажмите на свою фракцию, чтобы выбрать ее, затем подтвердите выбор';
  }
  return actingPlayer.factionId ? 'Фракцию можно сменить до старта матча' : 'Нажмите на свою фракцию, чтобы выбрать ее';
};

/**
 * Свои фракции рейтингового лобби — крупными плитками. Во время банов они не
 * выбираются: на них видно, кто из соперников голосует против них, нажатие
 * открывает карточку. После банов нажатие — выбор, забаненная обесцвечена и
 * не выбирается, кнопка «i» (и двойное нажатие) — карточка с «Выбрать».
 * Подтвердил выбор — плитки снова не выбираются (нажатие — карточка), пока не
 * нажмет «Изменить выбор».
 */
const OwnRankedFactions = ({
  actingPlayer, auth, banView, selectView, ownFactions, onOpenFactionCard, hiddenFactionsText,
}) => {
  const factionIds = actingPlayer.availableFactionIds || [];
  if (factionIds.length === 0) {
    return <div className="lobby-desktop-card-placeholder">{hiddenFactionsText}</div>;
  }

  const {selectFlash} = ownFactions;
  const tiles = factionIds.map((factionId) => {
    const skinId = actingPlayer.selectedSkins?.[factionId] || auth.factionLoadouts?.[factionId]?.selectedSkin;
    const display = getLobbyFactionDisplay(factionId, skinId);
    const openCard = () => onOpenFactionCard(actingPlayer, factionId);
    // В фазе банов своя плитка не выбирается и не голосуется — карточку
    // открывают двойное нажатие и «i», как и после банов (одиночное нажатие
    // открывало бы карточку, а второе нажатие двойного тут же ее закрывало)
    if (banView.isPhase) {
      return {
        factionId,
        display,
        voters: banView.getFactionVoters(actingPlayer.id, factionId),
        isInteractive: false,
        title: `${display.name}: двойное нажатие — карточка фракции`,
        onDoubleClick: openCard,
        onInfo: openCard,
      };
    }
    const isBanned = actingPlayer.bannedFactionId === factionId;
    const isSelected = actingPlayer.factionId === factionId;
    const isSelectable = !isBanned && !ownFactions.isSelectBlocked;
    // Забаненную подписывать незачем — это видно по знаку бана поверх арта
    const label = isSelected ? 'Выбрана' : null;
    const labelKind = isSelected ? 'selected' : undefined;
    let title = display.name;
    if (isSelectable) {
      title = `${display.name}: нажмите, чтобы выбрать`;
    } else if (selectView.isOwnConfirmed && !isBanned) {
      title = `${display.name}: выбор подтвержден — сменить его можно кнопкой «Изменить выбор»`;
    }
    return {
      factionId,
      display,
      voters: [],
      banMark: isBanned ? 'final' : null,
      banTitle: 'Фракция забанена',
      isSelected,
      label,
      labelKind,
      isInteractive: true,
      title,
      flashAt: selectFlash?.factionId === factionId ? selectFlash.at : null,
      // Забаненную не выбрать — нажатие по ней просто открывает карточку.
      // Выбор — с задержкой ради двойного нажатия (см. onTileSelect)
      onClick: isSelectable ? () => ownFactions.onTileSelect(factionId) : openCard,
      onDoubleClick: isSelectable ? openCard : undefined,
      onInfo: openCard,
    };
  });

  const isRandomSelected = actingPlayer.factionId === RANDOM_FACTION_ID;

  return (
    <div className="lobby-own-card_ranked">
      <LobbyFactionTiles tiles={tiles} className="lobby-own-card_tiles"/>
      <div className="lobby-own-card_ranked-footer">
        <span className="lobby-own-card_hint">{getOwnRankedFactionsHint({actingPlayer, banView, selectView})}</span>
        <PageButton
          className={`lobby-own-card_random ${isRandomSelected ? 'lobby-own-card_random_selected' : ''}`}
          // Звук и аналитика выбора — в самом обработчике
          noSound
          onClick={() => ownFactions.onSelect(RANDOM_FACTION_ID)}
          disabled={ownFactions.isSelectBlocked || isRandomSelected}
        >
          {isRandomSelected ? 'Выбрана случайная' : 'Случайная фракция'}
        </PageButton>
      </div>
    </div>
  );
};

/**
 * Своя карточка десктопного лобби — крупнее соперников, в золотой рамке.
 * Шапка: ник и бейджи; в обычном лобби справа — переключатель вида выбора
 * фракции. Ниже — встроенный выбор фракции: в обычном лобби полными артами
 * (карусель или все фракции сразу), в рейтинговом — плитки своей четверки.
 *
 * @param {Object} props
 * @param {Object} props.lobby
 * @param {Object} props.actingPlayer
 * @param {Object} props.auth
 * @param {Object} props.banView - состояние фазы банов из LobbyPage
 * @param {Object} props.selectView - окно выбора рейтингового лобби: подтверждение и готовность из LobbyPage
 * @param {Object} props.ownFactions - обработчики выбора из LobbyPage
 * @param {Function} props.onOpenFactionCard - (player, factionId) => void
 * @param {string} props.hiddenFactionsText - заглушка, пока фракции не выданы
 */
const LobbyOwnCard = ({
  lobby, actingPlayer, auth, banView, selectView, ownFactions, onOpenFactionCard, hiddenFactionsText,
}) => {
  const [pickerView, setPickerView] = useState(readStoredPickerView);

  const changePickerView = (view) => {
    if (view === pickerView) return;
    setPickerView(view);
    storePickerView(view);
  };

  return (
    <section className="lobby-own-card" data-player-id={actingPlayer.id}>
      <header className="lobby-desktop-card-header lobby-own-card_header">
        <div className="lobby-desktop-card-header_player">
          <LobbyPlayerName player={actingPlayer}/>
          <LobbyDesktopPlayerBadges
            player={actingPlayer}
            isOwn
            isLobbyCreator={actingPlayer.id === lobby.creatorId}
            banStatus={banView.getPlayerStatus(actingPlayer)}
            isReady={selectView.checkIfPlayerReady(actingPlayer)}
            // В окне выбора рейтингового лобби выбор без подтверждения еще не
            // решение — и у себя тоже «Выбирает фракцию»
            isChoosingShown={selectView.isWindow}
          />
        </div>
        {!lobby.isRanked && (
          <div className="lobby-view-switch" role="group" aria-label="Вид выбора фракции">
            {PICKER_VIEW_OPTIONS.map(({view, label, icon}) => (
              <button
                type="button"
                key={view}
                className={`lobby-view-switch_option ${pickerView === view ? 'lobby-view-switch_option_active' : ''}`}
                aria-pressed={pickerView === view}
                data-view={view}
                title={label}
                onClick={() => {
                  MENU_SOUND_MANAGER.playMenuButtonClick();
                  changePickerView(view);
                }}
              >
                {icon}
                <span className="lobby-view-switch_label">{label}</span>
              </button>
            ))}
          </div>
        )}
      </header>

      {lobby.isRanked ? (
        <OwnRankedFactions
          actingPlayer={actingPlayer}
          auth={auth}
          banView={banView}
          selectView={selectView}
          ownFactions={ownFactions}
          onOpenFactionCard={onOpenFactionCard}
          hiddenFactionsText={hiddenFactionsText}
        />
      ) : (
        <LobbyFactionPicker
          auth={auth}
          actingPlayer={actingPlayer}
          viewMode={pickerView}
          onViewModeChange={changePickerView}
          ownFactions={ownFactions}
        />
      )}
    </section>
  );
};

export default LobbyOwnCard;
